import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useProrrateos } from "@/features/prorrateo-multiple/hooks/use-prorrateos";
import { ProrrateoSeccion } from "@/features/prorrateo/components/ProrrateoSeccion";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Group, PageHeader, ReadOnlyBanner, Stack, Text } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { qk } from "@/lib/query-keys";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/prorrateo-multiple";
import { DispersionMultipleMenu } from "./DispersionMultipleMenu";
import { ProrrateoMultipleActions } from "./ProrrateoMultipleActions";

/**
 * Container del prorrateo múltiple (subtipo MULTIPLES_CFDI).
 * Renderiza N `<ProrrateoSeccion>` (compartido con el flujo único, ya
 * Tailwind) · una por cada CFDI en la solicitud, ordenados por slot.
 */
export function ProrrateoMultiplePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: sol } = useSolicitud(id);
  const { data: prorrateos } = useProrrateos(id);

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (P29) · sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const todosConfirmados = prorrateos.length === sol.cfdis.length && prorrateos.every((p) => p.estado === "Confirmado");

  const onCambio = () => {
    qc.invalidateQueries({ queryKey: qk.prorrateos(id) });
    qc.invalidateQueries({ queryKey: qk.solicitud(id) });
  };

  const cfdisOrdenados = sol.cfdis.slice().sort((a, b) => a.numeroSlot - b.numeroSlot);
  // La bandera de dispersión por Excel es por área de la solicitud (igual para todos
  // los slots) · basta mirar el primer prorrateo.
  const dispersionExcel = !soloLectura && (prorrateos[0]?.dispersionExcelHabilitada ?? false);

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title={`Prorrateos · Solicitud #${sol.numeroSolicitud}`}
        description={`${sol.cfdis.length} CFDIs · ${prorrateos.length} prorrateo(s) · edita las líneas y confirma cada CFDI individualmente.`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Prorrateos" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} paso="prorrateo" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="Los prorrateos no pueden modificarse." />}
          {dispersionExcel && (
            <Group justify="space-between" align="center" className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <Text size="sm" c="dimmed">
                Carga el desglose por centro de costo de todas las facturas desde una sola plantilla de Excel (columna “Número de factura”).
              </Text>
              <DispersionMultipleMenu solicitudId={id} cfdis={cfdisOrdenados} prorrateos={prorrateos} onCambio={onCambio} />
            </Group>
          )}
          {cfdisOrdenados.map((cfdi) => {
            const prorrateo = prorrateos.find((p) => p.numeroCfdi === cfdi.numeroSlot);
            return (
              <ProrrateoSeccion
                key={cfdi.numeroSlot}
                solicitudId={id}
                numeroCfdi={cfdi.numeroSlot}
                numeroSolicitud={sol.numeroSolicitud}
                uuid={cfdi.uuid}
                rfcEmisor={cfdi.rfcEmisor}
                subtotalCfdi={cfdi.subtotal}
                prorrateo={prorrateo ?? null}
                onCambio={onCambio}
                soloLectura={soloLectura}
              />
            );
          })}
          <ProrrateoMultipleActions
            todosConfirmados={todosConfirmados}
            soloLectura={soloLectura}
            onVolver={() => navigate({ to: "/solicitudes/$id/proveedor", params: { id }, replace: true })}
            onContinuar={() => navigate({ to: "/solicitudes/$id/documentos", params: { id } })}
          />
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
